<script lang="ts">
  /* Universal row actions: a pencil to edit, a cross to delete.
     Every table in the app uses this instead of "Edit" / "Delete" text
     buttons, so a row's controls look and sit the same everywhere.

     Either half is optional — pass only `onedit` for a read-only-ish row, or
     only `ondelete` where editing happens by opening the record. `editHref`
     renders the pencil as a link (for rows whose edit is a navigation). */
  import { _ } from "svelte-i18n";

  type Props = {
    onedit?: (e: MouseEvent) => void;
    ondelete?: (e: MouseEvent) => void;
    /** Renders the pencil as an <a> instead of a button. */
    editHref?: string;
    editTitle?: string;
    deleteTitle?: string;
    editDisabled?: boolean;
    deleteDisabled?: boolean;
  };

  let {
    onedit,
    ondelete,
    editHref,
    editTitle,
    deleteTitle,
    editDisabled = false,
    deleteDisabled = false,
  }: Props = $props();

  const editLabel = $derived(editTitle ?? $_("common.edit"));
  const deleteLabel = $derived(deleteTitle ?? $_("common.delete"));
</script>

<span class="row-actions">
  {#if editHref}
    <a class="row-action" href={editHref} title={editLabel} aria-label={editLabel}>
      <span class="material-icons-outlined" aria-hidden="true">edit</span>
    </a>
  {:else if onedit}
    <button
      type="button"
      class="row-action"
      onclick={onedit}
      disabled={editDisabled}
      title={editLabel}
      aria-label={editLabel}
    >
      <span class="material-icons-outlined" aria-hidden="true">edit</span>
    </button>
  {/if}
  {#if ondelete}
    <button
      type="button"
      class="row-action row-action--danger"
      onclick={ondelete}
      disabled={deleteDisabled}
      title={deleteLabel}
      aria-label={deleteLabel}
    >
      <span class="material-icons-outlined" aria-hidden="true">close</span>
    </button>
  {/if}
</span>

<style>
  .row-actions {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
  }
  .row-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    text-decoration: none;
    transition: background 0.12s, color 0.12s;
  }
  .row-action .material-icons-outlined { font-size: 18px; }
  .row-action:hover:not(:disabled) {
    background: rgba(33, 133, 213, 0.1);
    color: var(--color-primary);
  }
  .row-action--danger:hover:not(:disabled) {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
  .row-action:disabled { opacity: 0.35; cursor: default; }
  .row-action:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
</style>
